// #99 Responses (`resp`, scheduling call 8 and 14): the *Responses* section of a meeting event — one Person row per
// attendee with the response pill on the right: *Accepted* (ok), *Declined* (red), *Acknowledgment Pending* (neutral)
// and, after the Admin's threshold, *nonresponse* (warn). The organiser's row carries the *Coordinator* tag; a
// Declined row on a Final Interview Loop interviewer carries the *panel-change workflow* link. The viewer's own row,
// while it is pending, carries G2's AckPair (*Accept* · *Decline*) instead of the pill — on no other row (DC-23). A
// blocked pair is dashed and described by the matrix word the seat holds (DC-24); `mode="view"` shows pills only.

import { useId, type MouseEvent } from "react";
import { Person, Pill, Tag, TextLink, type Blocked } from "../core";
import { ACKNOWLEDGMENT_STATUS, RESPONSE_STATUS, ROLE_LABELS, type Tone } from "../labels";
import { cx } from "../lib/cx";
import { AckPair, type AckPairProps } from "../g2";
import { RESPONSE_LABELS } from "./labels";
import type { Hs } from "../lib/hs";

export type ResponseStatus = "accepted" | "declined" | "pending" | "nonresponse";

export const RESPONSE_PILL: Readonly<Record<ResponseStatus, { word: string; tone: Tone }>> = {
  accepted: { word: ACKNOWLEDGMENT_STATUS.accepted, tone: "ok" },
  declined: { word: ACKNOWLEDGMENT_STATUS.declined, tone: "red" },
  pending: { word: ACKNOWLEDGMENT_STATUS.acknowledgmentPending, tone: "neutral" },
  nonresponse: { word: RESPONSE_STATUS.nonresponse, tone: "warn" },
};

export interface ResponseItem {
  key: string;
  /** The attendee's name (data). */
  name: string;
  /** The second line: the seat in his role words, or the round. */
  sub?: string | undefined;
  hueKey?: string | undefined;
  candidate?: boolean | undefined;
  status: ResponseStatus;
  /** The organiser: the *Coordinator* tag. */
  coordinator?: boolean | undefined;
  /** The viewer's own row: Accept · Decline while pending. */
  own?: boolean | undefined;
  /** A Declined Final Interview Loop interviewer: the *panel-change workflow* link. */
  panelChange?: { href?: string | undefined; onOpen?: ((event: MouseEvent<HTMLElement>) => void) | undefined; hs?: Hs | undefined } | undefined;
}

export interface ResponsesProps {
  items: readonly ResponseItem[];
  /** The section's heading (default *Responses*). */
  title?: string | undefined;
  onAccept?: (() => void) | undefined;
  onDecline?: (() => void) | undefined;
  busy?: AckPairProps["busy"];
  /** The own row's pair blocked: the matrix word the seat holds as its description. */
  blocked?: Blocked | undefined;
  mode?: "edit" | "view" | undefined;
  can?: boolean | undefined;
  hs?: AckPairProps["hs"];
  className?: string | undefined;
}

const ACK_OF: Readonly<Record<ResponseStatus, NonNullable<AckPairProps["status"]>>> = {
  accepted: "accepted", declined: "declined", pending: "pending", nonresponse: "nonresponse",
};

function Status({ item, pair }: { item: ResponseItem; pair: Omit<AckPairProps, "status"> | null }) {
  // The own row keeps the pair's slot: once answered, the pair draws the answer and hands it the focus.
  if (pair && item.own) return <AckPair {...pair} status={ACK_OF[item.status]} />;
  const pill = RESPONSE_PILL[item.status];
  return <Pill tone={pill.tone}>{pill.word}</Pill>;
}

export function Responses({
  items, title = RESPONSE_LABELS.responses, onAccept, onDecline, busy, blocked, mode, can, hs, className,
}: ResponsesProps) {
  const id = useId();
  if (can === false) return null;
  const pair = mode === "view" ? null : { onAccept, onDecline, busy, blocked, hs };
  return (
    <section className={cx("mc-resp", className)} aria-labelledby={`${id}-h`}>
      <h4 className="mc-resp-h" id={`${id}-h`}>{title}</h4>
      <ul className="mc-resp-l" aria-label={RESPONSE_LABELS.responseStatus}>
        {items.map((item) => {
          const right = (
            <span className="mc-resp-r">
              {item.coordinator ? <Tag>{ROLE_LABELS.coordinator}</Tag> : null}
              <Status item={item} pair={pair} />
            </span>
          );
          const sub = item.panelChange && item.status === "declined" ? (
            <span className="mc-resp-sub">
              {item.sub !== undefined ? <span>{item.sub}</span> : null}
              <TextLink href={item.panelChange.href} onClick={item.panelChange.onOpen} after="right" hs={item.panelChange.hs}>
                {RESPONSE_LABELS.panelChangeWorkflow}
              </TextLink>
            </span>
          ) : item.sub;
          return (
            <li key={item.key} className={cx("mc-resp-i", item.own && "mc-resp-own")}>
              <Person name={item.name} hueKey={item.hueKey} candidate={item.candidate} sub={sub} right={right} />
            </li>
          );
        })}
      </ul>
    </section>
  );
}
